/**
 * AD-007n change 3: **the elevator commands path curvature, not a body pitch
 * rate.** The vertical-plane counterpart of AD-007h's produced turn, and the
 * load-bearing half of the flight-feel fix.
 *
 * The evidence is three traces flown with the stick fully back and nothing else
 * different but the bank (`docs/engineering/flight-feel-findings.md` §4c):
 *
 * | bank | pitch-attitude rate, original |
 * |---:|---:|
 * | 0° (`PITCH1`) | **+22 °/s** |
 * | 38° (`PULL1`) | **+4.5 °/s** |
 * | 70° (`PULL2`) | **−3.7 °/s** |
 *
 * A body-rate elevator gives `q·cos φ` = 22 / 17.3 / 7.5 °/s. It cannot produce
 * the negative number, and the original's is negative: at 70° of bank, full back
 * stick puts the nose *down*. Reading the same three traces as a flight-path
 * angle rate and solving the vertical-plane balance
 *
 * ```
 * γ̇ = g·(n·cos φ − cos γ) / V
 * ```
 *
 * for the implied load factor gives **n ≈ 1.8 / 1.5 / 1.7** across three
 * manoeuvres with nothing else in common. Full back stick in `RB.EXE` is a
 * bounded ~1.5–1.8 g pull *on the flight path*; the port's was an unbounded
 * 22 °/s rotation of the airframe, which reached 3.1 g at 26° of bank and 4.8 g
 * at 54° with 67° and 83° of angle of attack.
 *
 * **What this module produces is only the part the wing cannot.** The force
 * model already curves the path with lift and gravity, and that pathway is
 * exactly `γ̇ = g(n_aero·cos φ − cos γ)/V` with `n_aero = L/W`. So the produced
 * term is the *difference* between the load factor the stick is asking for and
 * the one the wing is delivering:
 *
 * ```
 * Δγ̇ = g · cos φ · pull · max(0, n_command − n_aero) / V
 * ```
 *
 * That shape is what makes the change safe as well as right:
 *
 * - **It is identically zero with the stick centred**, so hands-off flight,
 *   trim, the §8.5.1 bank sweep, the spiral knee, the rudder holds and the wind
 *   traces are all bit-identical to what AD-007l/m shipped. Every regression
 *   rail that does not involve the elevator is untouched *by construction*
 *   rather than by measurement.
 * - **It is zero whenever the wing is already delivering the command**, which at
 *   cruise it very nearly is: a 1.7 g pull at 97 mph is an ordinary aerodynamic
 *   pull, and the produced term only fills in as the speed decays past the point
 *   where the wing can hold it. That is the regime the original is unphysical in
 *   — it sustains 1.66 g at 50 mph where the Camel's own lift curve tops out at
 *   1.38 — and this is where the port now matches it.
 * - **It never caps a pull the wing *can* make.** Hauling out of a dive at 3 g
 *   is untouched, so structural failure stays reachable exactly as before.
 *
 * `cos φ` carries the bank all the way to a knife edge, where it is zero and a
 * pull curves nothing vertically, and past it, where it is negative and a pull
 * pulls the nose *down* — which is inverted flight behaving.
 *
 * **The rate ceiling is §8.1's own measurement, and this is where it lands.**
 * The session timed a full-elevator pitch rate of 22.1 °/s (Camel) and 21.0 °/s
 * (SPAD XIII). Those stay the elevator's authority, now as a **limit on how fast
 * the path can be rotated** rather than as the rate itself, faded by
 * `airspeedAuthority` like any other pitch command. The two formulations agree
 * where the session measured them and separate where it did not: `PITCH1`'s own
 * attitude rate "ramps 11 → 22 °/s over ≈2.3 s" (`re-sessions/feel/NOTES.md`
 * §4), which is exactly path curvature at 97 mph growing into the 22 °/s ceiling
 * as the zoom bleeds speed — `g(1.8 − 1)/43.3` is 10.4 °/s and `g(1.8 −
 * cos 43°)/25` is 20.6.
 */

import { postStallFactor, type AeroSpec } from "./aero.ts";
import { airspeedAuthority } from "./controls.ts";
import type { Tunables } from "./tunables.ts";
import { GRAVITY_MPS2, clamp } from "./units.ts";

export interface PathPitchDemand {
  readonly aero: AeroSpec;
  readonly tunables: Tunables;
  /** Signed bank angle, radians, right-positive — the `cos φ` of the balance. */
  readonly bankRad: number;
  /** Elevator position, −1 full forward to +1 full back. */
  readonly pitchInput: number;
  /** What the wing is already delivering, `L/W` (`forces.ts`'s `loadFactor`). */
  readonly loadFactor: number;
  readonly airspeedMps: number;
  readonly alphaRad: number;
  /** The elevator's own rate ceiling, rad/s — flight-tunables.md §8.1's 22.1 °/s on the Camel. */
  readonly pitchRateMaxRadPerSec: number;
  /** 0..1 control-surface health, so a shot-away elevator commands less path too. */
  readonly controlAuthority: number;
}

/**
 * The load factor the stick is asking the flight path for. Back stick runs from
 * the wing's own `n_aero` up to `elevatorLoadFactorMax`; forward stick runs the
 * other way toward `elevatorLoadFactorMin`, which is what a bunt is.
 */
export function commandedLoadFactor(demand: PathPitchDemand): number {
  const stick = clamp(demand.pitchInput, -1, 1);
  const { tunables } = demand;
  const target = stick >= 0 ? tunables.elevatorLoadFactorMax : tunables.elevatorLoadFactorMin;
  return demand.loadFactor + Math.abs(stick) * (target - demand.loadFactor);
}

export interface AlphaReferenceDemand {
  readonly tunables: Tunables;
  /** Stall break, radians — the reference is a fraction of it and never past it. */
  readonly alphaStallRad: number;
  /** Elevator position, −1 full forward to +1 full back. */
  readonly pitchInput: number;
  /** The angle of attack the wing is at now — where a centred stick leaves it. */
  readonly alphaRad: number;
}

/**
 * AD-007n change 2: **the angle of attack the pilot is asking for** — what
 * `controls.ts`'s α-relaxation now relaxes *toward*, and the reason that
 * relaxation can finally be ungated.
 *
 * An ungated relaxation toward **zero** would sag the nose out of trimmed
 * flight: the port's Camel cruises at α = 3.8°, which is the α its wing needs
 * for 1 g, not an error to be closed. So the stick asks for an angle of attack
 * rather than for an angle:
 *
 * - **stick centred** ⇒ the reference is the α the wing is already at ⇒ the
 *   relaxation is **exactly zero**, so trim stays bit-constant and every
 *   hands-off rail (§8.5.1's thirteen banks, the spiral knee, the rudder holds,
 *   the wind traces) is untouched by construction rather than by measurement;
 * - **full back** ⇒ the reference is `alphaFollowStallFraction` of the stall
 *   break, and α settles there instead of running away to 67°. That is the
 *   single number that takes the every-airframe-departs-in-four-seconds census
 *   to zero, and it does it **without touching a stall or spin tunable**;
 * - **partial stick** interpolates between the two, which is what makes the
 *   shaped keyboard axis buy a proportional pull rather than an on/off one.
 *
 * **The bound is on the angle, not on the g.** That distinction is the whole
 * reason this is `α` and not `n`: the same 30 % of the break is 1.7 g at cruise,
 * a fraction of a g at 50 mph — where `producedPathPitchRateRadPerSec` above
 * makes up the difference, because the original sustains 1.66 g at 50 mph
 * against its own wing's 1.38 g maximum — and **7 g at 200 mph**, which is how
 * the manual's "too much stress during a high speed dive" goes on tearing wings
 * off. A bound on the load factor would have capped that at cruise's 1.7 g and
 * made the airframe unbreakable.
 */
export function alphaReferenceRad(demand: AlphaReferenceDemand): number {
  const stick = clamp(demand.pitchInput, -1, 1);
  if (stick === 0) return demand.alphaRad;
  const ceiling = Math.abs(demand.alphaStallRad * demand.tunables.alphaFollowStallFraction);
  return demand.alphaRad + Math.abs(stick) * (Math.sign(stick) * ceiling - demand.alphaRad);
}

/**
 * The produced flight-path pitch rate for this tick, rad/s, nose-up-positive.
 * `step.ts` applies it as a rigid rotation of the velocity *and* the orientation
 * about the path's world-horizontal axis, so the angle of attack is carried
 * through it unchanged (`math3d.ts`'s `pitchAboutWorldAxis`/`applyWorldPitch`).
 */
export function producedPathPitchRateRadPerSec(demand: PathPitchDemand): number {
  const stick = clamp(demand.pitchInput, -1, 1);
  if (stick === 0) return 0;
  const speed = demand.airspeedMps;
  if (speed < 1e-3) return 0;

  // Only the shortfall is produced: back stick that the wing is already holding
  // (or beating) asks for nothing, and neither does forward stick on a wing
  // already unloaded past the command.
  const commanded = commandedLoadFactor(demand);
  const shortfall = stick >= 0 ? Math.max(0, commanded - demand.loadFactor) : Math.min(0, commanded - demand.loadFactor);
  if (shortfall === 0) return 0;

  const authority =
    airspeedAuthority(speed, demand.tunables) *
    postStallFactor(demand.aero, demand.alphaRad) *
    clamp(demand.controlAuthority, 0, 1);
  const rate = (GRAVITY_MPS2 * Math.cos(demand.bankRad) * shortfall * authority) / speed;
  // §8.1's measured elevator ceiling, now bounding the path rather than the
  // airframe. Without it the `1/V` in the balance grows without bound as a zoom
  // decays — the same unbounded-angular-effect trap flight-tunables.md §8.2
  // found in nose-aligned thrust.
  const ceiling = demand.pitchRateMaxRadPerSec * Math.abs(stick) * authority;
  return clamp(rate, -ceiling, ceiling);
}

/**
 * AD-007p: **the elevator's rate ceiling belongs to the attitude, because that
 * is the quantity it was measured on.**
 *
 * `producedPathPitchRateRadPerSec` above clamps the *path*. But the elevator
 * rotates the nose along two pathways, not one — the path curvature it produces,
 * and `controls.ts`'s α-relaxation closing the nose onto `alphaReferenceRad`
 * (change 2) — and what §8.1 timed with a stopwatch against the artificial
 * horizon is their **sum**. Clamping one of two addends is the same class of
 * mistake AD-007o §8.2 found in three other rails: a ceiling applied to a
 * quantity next to the one it was taken from.
 *
 * The original's nose-up attitude rate is capped at 21.0–22.5 °/s by four
 * independent DOSBox readings, and they agree far better than the port did:
 *
 * | trace | peak θ̇ |
 * |---|---:|
 * | §8.1's timed full-elevator pull (Camel) | 22.1 °/s |
 * | §8.1's same, SPAD XIII | 21.0 °/s |
 * | `PITCH1` re-flown (`mem/P1*.bin`, 0.82 s samples) | **22.50 °/s** |
 * | the 18.5 s zoom (`mem/Z1*.bin`, 1.54 s samples) | 21.5 °/s |
 *
 * The leak grows with `alphaFollowStallFraction`, which is why it had never been
 * seen: at 0.30 the relaxation has already gone *negative* by the time the path
 * term reaches its ceiling, so the peak sum is 23.0 °/s and only 4 % over. Push
 * the fraction up and the two stack instead — at 0.33 the attitude reaches
 * 24.1 °/s, at 0.48 the path sits pinned at 22.1 from t = 3.5 s while the
 * relaxation is still adding 5.5–6.5 and the nose rotates at **29.1 °/s, 29 %
 * past a number measured four times**. Over the second that lasts it is ~9° of
 * extra nose-up, which is why the port stood on its tail at 88.6° where the
 * original's zoom peaks at 70.2°.
 *
 * **This is what lets the fraction move at all.** Without it, 0.33 puts the
 * S.S.W. D.III into an autorotation out of §4.5's twenty-second zoom; with it,
 * the census is clean fleet-wide on both stick shapes. AD-007p has the sweep.
 *
 * **The path term keeps the whole ceiling and the relaxation takes the trim.**
 * That is not a preference: the produced path rate curves the *velocity*, so
 * trimming it would change the trajectory, while the relaxation is orientation-
 * only (`kinematics.ts`) and trimming it changes only how far the airframe is
 * allowed to run ahead of a flight path that is already going where it goes.
 *
 * Zero-effect with the stick centred, by construction and not by measurement:
 * both addends are identically zero there — the produced rate short-circuits on
 * `stick === 0` and `alphaReferenceRad` returns the current α, so the relaxation
 * has nothing to close — so the sum never reaches the ceiling and every hands-off
 * rail is untouched.
 *
 * **It bounds only the case where the two pathways *add*, and that boundary was
 * measured rather than reasoned to.** Opposite signs mean the relaxation is
 * working *against* the stick — a stalled nose falling while the elevator is
 * still asking for up — which is not an elevator rotating an airframe and
 * carries no elevator ceiling. The archive agrees: over the top of the same
 * zoom, stick still hard back, `PITCH1` drops its nose at −17.5 and −30.0 °/s
 * and the Z-series at −15.2 and −39.1, all well past 22.1.
 *
 * Bounding that direction too was tried and §4.5's nose-sag rail rejects it: the
 * port then crosses +24.7° at **33 mph** where the original's own continuous
 * hold crosses it at ≈16 mph and the rail's band is 15–25. Constrained to the
 * adding case, the same flight crosses at 22.5 mph.
 */
export function boundedAlphaRelaxRadPerSec(
  relaxPitchRateRadPerSec: number,
  pathPitchRateRadPerSec: number,
  pitchRateMaxRadPerSec: number,
): number {
  // Only where the two *add*. Opposite signs are the relaxation working against
  // the stick — a stalled nose falling while the elevator is still asking for up
  // — which is not the elevator rotating the airframe at all and carries no
  // elevator ceiling. See the note above.
  if (relaxPitchRateRadPerSec * pathPitchRateRadPerSec < 0) return relaxPitchRateRadPerSec;
  const ceiling = Math.abs(pitchRateMaxRadPerSec);
  const total = pathPitchRateRadPerSec + relaxPitchRateRadPerSec;
  if (Math.abs(total) <= ceiling) return relaxPitchRateRadPerSec;
  // The path term is served first; the relaxation gets whatever headroom is
  // left, which is zero once the path alone is at the ceiling.
  const headroom = Math.max(0, ceiling - Math.abs(pathPitchRateRadPerSec));
  return clamp(relaxPitchRateRadPerSec, -headroom, headroom);
}
